import {envConfig} from 'config';

import {logger} from './logger';

// LVC-3190 investigation aid. The crash is `portalContainer.removeChild(child)` thrown from React's commit phase,
// so its stack contains React frames only — the code that actually left the DOM inconsistent ran earlier and is
// invisible by the time it explodes. This records every hand-made mutation touching a modal portal, so the crash
// can be read together with what happened to that portal just before it.
const PORTAL_SELECTOR = '.ReactModalPortal';
const HISTORY_LIMIT = 50;
const PATCHED_METHODS = ['appendChild', 'insertBefore', 'removeChild'] as const;

type PatchedMethod = typeof PATCHED_METHODS[number];

type PortalMutation = {
    method: PatchedMethod;
    parent: string;
    node: string;
    stack: string;
};

const history: PortalMutation[] = [];
let started = false;

const describe = (node: Node): string => {
    const element = node as Element;
    if (!element?.tagName) {
        return node?.nodeName || String(node);
    }
    const classes = typeof element.className === 'string' && element.className
        ? `.${element.className.trim().split(/\s+/).join('.')}`
        : '';
    return `${element.tagName.toLowerCase()}${element.id ? `#${element.id}` : ''}${classes}`.slice(0, 200);
};

const isInPortal = (node: Node): boolean => {
    const element = node as Element;
    return typeof element?.closest === 'function' && !!element.closest(PORTAL_SELECTOR);
};

const record = (method: PatchedMethod, parent: Node, node: Node) => {
    if (history.length >= HISTORY_LIMIT) {
        history.shift();
    }
    history.push({
        method,
        parent: describe(parent),
        node: describe(node),
        stack: new Error().stack || ''
    });
};

export const portalMutationTracer = {
    start: () => {
        if (!envConfig.tracePortalMutations || started) {
            return;
        }
        started = true;

        PATCHED_METHODS.forEach((method) => {
            const original = Node.prototype[method] as (...args: Node[]) => Node;
            (Node.prototype as any)[method] = function patched(this: Node, ...args: Node[]) {
                const node = args[0];
                // Recorded before delegating, so a call that throws still leaves its trace behind.
                if (isInPortal(this) || isInPortal(node)) {
                    record(method, this, node);
                }
                return original.apply(this, args);
            };
        });

        logger.warn('Portal mutation tracer started');
    },

    dump: (reason: string) => {
        if (!started || !history.length) {
            return;
        }
        logger.warn(`Portal mutations before "${reason}"`, {mutations: [...history]});
    }
};
